import { User, Calendar, Clock } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { Verification } from "../../types";

interface VerificationLogProps {
  requests: Verification[];
}

const VerificationLog = ({ requests }: VerificationLogProps) => {
  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return (
      date.toLocaleDateString() +
      " at " +
      date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    );
  };

  if (requests.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="pt-6 text-center text-gray-500">
          <p>No processed verification requests</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {requests.map((request) => (
        <Card
          key={request.id}
          className={`border-l-4 ${
            request.status === "approved"
              ? "border-l-green-400"
              : request.status === "rejected"
                ? "border-l-red-400"
                : request.status === "expired"
                  ? "border-l-gray-400"
                  : "border-l-yellow-400"
          }`}
        >
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="flex justify-between items-start">
                <h3 className="font-medium font-mono text-xs">{request.id}</h3>
                <Badge
                  variant="outline"
                  className={
                    request.status === "approved"
                      ? "bg-green-50 text-green-700 border-green-200"
                      : request.status === "rejected"
                        ? "bg-red-50 text-red-700 border-red-200"
                        : request.status === "expired"
                          ? "bg-gray-50 text-gray-700 border-gray-200"
                          : "bg-yellow-50 text-yellow-700 border-yellow-200"
                  }
                >
                  {request.status.charAt(0).toUpperCase() +
                    request.status.slice(1)}
                </Badge>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center">
                  <User className="h-4 w-4 mr-2 text-gray-500" />
                  <span className="font-medium">From:</span>
                  <span className="ml-2 font-mono text-xs">
                    {request.requester_id}
                  </span>
                </div>

                {request.decided_at && (
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-2 text-gray-500" />
                    <span className="font-medium">Decided:</span>
                    <span className="ml-2">
                      {formatDate(request.decided_at)}
                    </span>
                  </div>
                )}

                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-2 text-gray-500" />
                  <span className="font-medium">Requested:</span>
                  <span className="ml-2">
                    {formatDate(request.requested_at)}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default VerificationLog;
